import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import api from "../utils/api";
import {
  Building2, Users, Plus, Edit2, Trash2, X, Check,
  Upload, Search, ChevronDown, AlertCircle, Download, Activity
} from "lucide-react";

const TABS = ["Departments", "Users", "Audit Log"];

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "480px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "var(--text-primary)", margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <label style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>{label}</label>
      {children}
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [tab, setTab] = useState("Departments");
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {type, data}
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");
  const fileRef = useRef();

  const loadAudit = async () => {
    try {
      const r = await api.get("/audit/", { params: { limit: 300 } });
      setAuditLogs(r.data.logs || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (tab === "Audit Log") loadAudit();
  }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, uRes] = await Promise.all([
        api.get("/admin/departments"),
        api.get("/admin/users"),
      ]);
      setDepartments(dRes.data);
      setUsers(uRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openModal = (type, data = {}) => { setModal({ type, data }); setForm(data); setError(""); };
  const closeModal = () => { setModal(null); setForm({}); setError(""); };

  const handleDeptSave = async () => {
    setError("");
    try {
      if (modal.type === "edit-dept") {
        await api.put(`/admin/departments/${form.id}`, { name: form.name, description: form.description });
      } else {
        await api.post("/admin/departments", { name: form.name, code: form.code, description: form.description });
      }
      await load();
      closeModal();
    } catch (e) { setError(e.response?.data?.detail || "Failed to save"); }
  };

  const handleUserSave = async () => {
    setError("");
    try {
      if (modal.type === "edit-user") {
        await api.put(`/admin/users/${form.id}`, {
          name: form.name, role: form.role,
          department_id: form.department_id || null,
          is_active: form.is_active,
        });
      } else {
        await api.post("/admin/users", {
          email: form.email, name: form.name, password: form.password,
          role: form.role || "dept_user", department_id: form.department_id || null,
        });
      }
      await load();
      closeModal();
    } catch (e) { setError(e.response?.data?.detail || "Failed to save"); }
  };

  const deactivateDept = async (id) => {
    if (!window.confirm("Deactivate this department?")) return;
    await api.delete(`/admin/departments/${id}`);
    load();
  };

  const toggleUser = async (u) => {
    await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    const fd = new FormData();
    fd.append("file", bulkFile);
    try {
      const res = await api.post("/admin/users/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Created ${res.data.created} users. ${res.data.errors.length} errors.`);
      setBulkFile(null);
      load();
      closeModal();
    } catch (e) { setError(e.response?.data?.detail || "Upload failed"); }
  };

  const downloadTemplate = () => {
    const csv = "email,name,password,role,department_code\njohn@example.com,John Doe,Pass@123,dept_user,FIN\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bulk_users_template.csv"; a.click();
  };

  const filteredDepts = departments.filter(d => d.name?.toLowerCase().includes(search.toLowerCase()) || d.code?.toLowerCase().includes(search.toLowerCase()));
  const filteredUsers = users.filter(u => u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout>
      <div className="animate-in">
        <div style={{ marginBottom: "2rem" }}>
          <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", margin: 0 }}>System Administration</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "0.25rem" }}>Manage departments, users, and roles</p>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          {[
            { label: "Departments", value: departments.length, icon: Building2 },
            { label: "Total Users", value: users.length, icon: Users },
            { label: "Active Users", value: users.filter(u => u.is_active).length, icon: Check },
          ].map(s => (
            <div key={s.label} className="recon-card" style={{ padding: "1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ width: "2.5rem", height: "2.5rem", borderRadius: "0.5rem", background: "var(--sage-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <s.icon size={18} style={{ color: "var(--sage)" }} strokeWidth={1.5} />
              </div>
              <div>
                <p style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "Bricolage Grotesque, sans-serif", color: "var(--text-primary)", margin: 0, lineHeight: 1 }}>{s.value}</p>
                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: 0, marginTop: "0.25rem" }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1rem" }}>
            <div style={{ display: "flex" }}>
              {TABS.map(t => (
                <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => { setTab(t); setSearch(""); }}
                  style={{
                    padding: "0.875rem 1rem", fontSize: "0.875rem", fontWeight: 500,
                    border: "none", background: "none", cursor: "pointer",
                    borderBottom: tab === t ? "2px solid var(--sage)" : "2px solid transparent",
                    color: tab === t ? "var(--sage)" : "var(--text-muted)",
                    transition: "all 0.15s",
                  }}>
                  {t}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <div style={{ position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: "0.625rem", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input data-testid="search-input" className="recon-input" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={`Search ${tab.toLowerCase()}...`}
                  style={{ paddingLeft: "2rem", fontSize: "0.8rem", height: "2rem" }} />
              </div>
              {tab === "Departments" ? (
                <button data-testid="add-dept-btn" className="btn-primary" onClick={() => openModal("add-dept")} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                  <Plus size={14} /> Department
                </button>
              ) : tab === "Users" ? (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button data-testid="bulk-upload-btn" className="btn-secondary" onClick={() => openModal("bulk-upload")} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                    <Upload size={14} /> Bulk Upload
                  </button>
                  <button data-testid="add-user-btn" className="btn-primary" onClick={() => openModal("add-user")} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                    <Plus size={14} /> User
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Table */}
          {tab === "Departments" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Name", "Code", "Description", "Status", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDepts.map(d => (
                  <tr key={d.id} className="table-row">
                    <td className="table-cell" style={{ fontWeight: 500 }}>{d.name}</td>
                    <td className="table-cell"><span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.8rem", background: "var(--bg-subtle)", padding: "0.125rem 0.5rem", borderRadius: "0.25rem" }}>{d.code}</span></td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{d.description || "—"}</td>
                    <td className="table-cell"><span className={d.is_active ? "badge-matched" : "badge-unmatched"}>{d.is_active ? "Active" : "Inactive"}</span></td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button data-testid={`edit-dept-${d.id}`} onClick={() => openModal("edit-dept", d)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Edit2 size={14} /></button>
                        <button data-testid={`del-dept-${d.id}`} onClick={() => deactivateDept(d.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredDepts.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>No departments found</td></tr>
                )}
              </tbody>
            </table>
          ) : tab === "Users" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Name", "Email", "Role", "Department", "Status", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id} className="table-row">
                    <td className="table-cell" style={{ fontWeight: 500 }}>{u.name}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{u.email}</td>
                    <td className="table-cell">
                      <span className={u.role === "dept_admin" ? "badge-partial" : "badge-warning"} style={{ textTransform: "capitalize" }}>
                        {u.role === "dept_admin" ? "Dept Admin" : "Analyst"}
                      </span>
                    </td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{u.department_name || "—"}</td>
                    <td className="table-cell"><span className={u.is_active ? "badge-matched" : "badge-unmatched"}>{u.is_active ? "Active" : "Inactive"}</span></td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button data-testid={`edit-user-${u.id}`} onClick={() => openModal("edit-user", { ...u, department_id: u.department_id || "" })} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Edit2 size={14} /></button>
                        <button data-testid={`toggle-user-${u.id}`} onClick={() => toggleUser(u)} style={{ background: "none", border: "none", cursor: "pointer", color: u.is_active ? "#DC2626" : "var(--sage)" }}>
                          {u.is_active ? <Trash2 size={14} /> : <Check size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          ) : null}
          {tab === "Audit Log" && (
            <div data-testid="audit-log-tab">
              <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Activity size={14} style={{ color: "var(--sage)" }} />
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                  {auditLogs.length} recent events
                </span>
                <input data-testid="audit-filter" className="recon-input" style={{ fontSize: "0.8rem", height: "1.75rem", marginLeft: "auto", width: "220px" }}
                  placeholder="Filter by action, email, or target..."
                  value={auditFilter} onChange={e => setAuditFilter(e.target.value)} />
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Time", "User", "Role", "Action", "Target", "Details"].map(h => (
                      <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs
                    .filter(l => !auditFilter ||
                      (l.action || "").toLowerCase().includes(auditFilter.toLowerCase()) ||
                      (l.user_email || "").toLowerCase().includes(auditFilter.toLowerCase()) ||
                      (l.target_type || "").toLowerCase().includes(auditFilter.toLowerCase()))
                    .map(l => (
                    <tr key={l.id} className="table-row">
                      <td className="table-cell" style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontFamily: "JetBrains Mono, monospace", whiteSpace: "nowrap" }}>
                        {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.82rem", fontWeight: 500 }}>{l.user_email || "—"}</td>
                      <td className="table-cell">
                        <span style={{ fontSize: "0.7rem", padding: "0.1rem 0.5rem", background: "var(--bg-subtle)", borderRadius: "9999px" }}>
                          {l.user_role || "—"}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span style={{ fontSize: "0.78rem", fontFamily: "JetBrains Mono, monospace", color: "var(--sage)", fontWeight: 500 }}>{l.action}</span>
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontFamily: "JetBrains Mono, monospace" }}>
                        {l.target_type}{l.target_id ? ` / ${l.target_id.slice(-8)}` : ""}
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "JetBrains Mono, monospace", maxWidth: "360px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.metadata ? JSON.stringify(l.metadata).slice(0, 120) : ""}
                      </td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>No audit events yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dept Modal */}
      <Modal open={modal?.type === "add-dept" || modal?.type === "edit-dept"} onClose={closeModal}
        title={modal?.type === "edit-dept" ? "Edit Department" : "Add Department"}>
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.625rem", marginBottom: "1rem", fontSize: "0.875rem", color: "#991B1B" }}>{error}</div>}
        <Field label="Department Name">
          <input data-testid="dept-name-input" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Finance" />
        </Field>
        {modal?.type !== "edit-dept" && (
          <Field label="Code (short identifier)">
            <input data-testid="dept-code-input" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
              value={form.code || ""} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. FIN" maxLength={10} />
          </Field>
        )}
        <Field label="Description (optional)">
          <input className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" />
        </Field>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
          <button className="btn-secondary" onClick={closeModal}>Cancel</button>
          <button data-testid="save-dept-btn" className="btn-primary" onClick={handleDeptSave}>Save</button>
        </div>
      </Modal>

      {/* User Modal */}
      <Modal open={modal?.type === "add-user" || modal?.type === "edit-user"} onClose={closeModal}
        title={modal?.type === "edit-user" ? "Edit User" : "Add User"}>
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.625rem", marginBottom: "1rem", fontSize: "0.875rem", color: "#991B1B" }}>{error}</div>}
        <Field label="Full Name">
          <input data-testid="user-name-input" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </Field>
        {modal?.type !== "edit-user" && (
          <Field label="Email">
            <input data-testid="user-email-input" type="email" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
              value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </Field>
        )}
        {modal?.type !== "edit-user" && (
          <Field label="Password">
            <input data-testid="user-password-input" type="password" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
              value={form.password || ""} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          </Field>
        )}
        <Field label="Role">
          <select data-testid="user-role-select" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={form.role || "dept_user"} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="dept_user">Analyst (Dept User)</option>
            <option value="dept_admin">Dept Admin</option>
          </select>
        </Field>
        <Field label="Department">
          <select data-testid="user-dept-select" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={form.department_id || ""} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
            <option value="">— No Department —</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name} ({d.code})</option>)}
          </select>
        </Field>
        {modal?.type === "edit-user" && (
          <Field label="Status">
            <select className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
              value={form.is_active ? "active" : "inactive"} onChange={e => setForm(f => ({ ...f, is_active: e.target.value === "active" }))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
        )}
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
          <button className="btn-secondary" onClick={closeModal}>Cancel</button>
          <button data-testid="save-user-btn" className="btn-primary" onClick={handleUserSave}>Save</button>
        </div>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal open={modal?.type === "bulk-upload"} onClose={closeModal} title="Bulk Upload Users">
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.625rem", marginBottom: "1rem", fontSize: "0.875rem", color: "#991B1B" }}>{error}</div>}
        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>Upload a CSV file with columns: email, name, password, role, department_code</p>
          <button className="btn-secondary" onClick={downloadTemplate} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
            <Download size={14} /> Download Template
          </button>
        </div>
        <div className="dropzone" style={{ padding: "2rem", textAlign: "center" }}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); setBulkFile(e.dataTransfer.files[0]); }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => setBulkFile(e.target.files[0])} />
          <Upload size={24} style={{ color: "var(--text-muted)", margin: "0 auto 0.5rem" }} />
          <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", margin: 0 }}>
            {bulkFile ? bulkFile.name : "Click or drag CSV file here"}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1rem" }}>
          <button className="btn-secondary" onClick={closeModal}>Cancel</button>
          <button data-testid="bulk-upload-submit" className="btn-primary" onClick={handleBulkUpload} disabled={!bulkFile}>Upload</button>
        </div>
      </Modal>
    </Layout>
  );
}
